import React, { useState, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Search as SearchIcon, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const allProducts = [
  { id: 'lifemail', title: 'lifemail', category: 'AI Email', desc: 'AI email client with memory.', status: 'beta' },
  { id: 'coldmail-ai', title: 'ColdMail AI', category: 'AI Email', desc: 'Personalized cold emails.', status: 'live' },
  { id: 'follow-up-sequence', title: 'Sequence Writer', category: 'AI Email', desc: 'Full sales cadences.', status: 'live' },
  { id: 'subject-line-optimizer', title: 'Subject Line Optimizer', category: 'AI Email', desc: 'Score subject lines by open rate.', status: 'beta' },
  { id: 'inbox-zero-coach', title: 'Inbox Zero Coach', category: 'AI Email', desc: 'Triage and batch your inbox.', status: 'building' },
  { id: 'linkedin-lead-scraper', title: 'Lead Qualifier', category: 'Lead Generation', desc: 'LinkedIn scraping and scoring.', status: 'live' },
  { id: 'local-business-finder', title: 'Local Lead Finder', category: 'Lead Generation', desc: 'Find local service businesses.', status: 'live' },
  { id: 'email-finder-verifier', title: 'Email Verifier', category: 'Lead Generation', desc: 'Verify deliverability.', status: 'beta' },
  { id: 'competitor-monitor', title: 'Competitor Monitor', category: 'Lead Generation', desc: 'Track competitor changes.', status: 'building' },
  { id: 'review-response-generator', title: 'Review Response Generator', category: 'Lead Generation', desc: 'Reply to reviews instantly.', status: 'building' },
  { id: 'blog-post-generator', title: 'Blog Post Generator', category: 'Content', desc: 'SEO content from briefs.', status: 'beta' },
  { id: 'linkedin-post-writer', title: 'LinkedIn Post Writer', category: 'Content', desc: 'Executive ghostwriting.', status: 'beta' },
  { id: 'newsletter-builder', title: 'Newsletter Builder', category: 'Content', desc: 'Curate from RSS and notes.', status: 'building' },
  { id: 'faq-builder', title: 'FAQ Builder', category: 'Content', desc: 'Turn tickets into knowledge base.', status: 'building' },
  { id: 'contract-scanner', title: 'Contract Scanner', category: 'Documents', desc: 'Flag risky clauses.', status: 'beta' },
  { id: 'resume-parser', title: 'Resume Parser', category: 'Documents', desc: 'Extract structured resume data.', status: 'live' },
  { id: 'proposal-writer', title: 'Proposal Writer', category: 'Documents', desc: 'Client brief to proposal.', status: 'building' },
  { id: 'pdf-summarizer', title: 'PDF Summarizer', category: 'Documents', desc: 'Summarize long PDFs in 60s.', status: 'beta' },
  { id: 'meeting-notes-ai', title: 'Meeting Notes AI', category: 'Voice & Meeting', desc: 'Transcribe and summarize.', status: 'beta' },
  { id: 'voice-memo-transcriber', title: 'Voice Memo Transcriber', category: 'Voice & Meeting', desc: 'Voice to structured text.', status: 'building' },
  { id: 'social-media-scheduler', title: 'Social Media Scheduler', category: 'Social', desc: 'Queue and auto-post.', status: 'building' },
  { id: 'hashtag-researcher', title: 'Hashtag Researcher', category: 'Social', desc: 'Find effective hashtags.', status: 'building' },
  { id: 'data-cleaner', title: 'Data Cleaner', category: 'Operations', desc: 'Fix and dedupe CSVs.', status: 'live' },
  { id: 'invoice-generator', title: 'Invoice Generator', category: 'Operations', desc: 'Time entries to invoice.', status: 'live' },
  { id: 'api-tester', title: 'API Tester', category: 'Operations', desc: 'Generate test cases.', status: 'beta' },
  { id: 'feature-request-prioritizer', title: 'Feature Prioritizer', category: 'Operations', desc: 'Score requests by impact.', status: 'building' },
  { id: 'onboarding-email-sequence', title: 'Onboarding Sequence', category: 'Niche', desc: 'Trial users to customers.', status: 'building' },
  { id: 'churn-prediction-dashboard', title: 'Churn Dashboard', category: 'Niche', desc: 'Predict and prevent churn.', status: 'building' },
];

const allPages = [
  { title: 'Products', category: 'Navigation', desc: 'Full catalog of 28 AI tools.', url: '/projects' },
  { title: 'About', category: 'Navigation', desc: 'What TaskTide builds and how.', url: '/about' },
  { title: 'Mission', category: 'Navigation', desc: 'One tool. One job. Done right.', url: '/about/mission' },
  { title: 'Technology', category: 'Navigation', desc: 'Stack, pipeline, and approach.', url: '/about/technology' },
  { title: 'Contact', category: 'Navigation', desc: 'Get in touch.', url: '/contact' },
];

const statusDot = (status: string) => {
  const map: Record<string, string> = {
    live: 'bg-emerald-400',
    beta: 'bg-amber-400',
    building: 'bg-slate-400'
  };
  return <span className={`h-2 w-2 rounded-full ${map[status] || map.building}`} />;
};

const Search = () => {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');

  React.useEffect(() => {
    const query = new URLSearchParams(location.search).get('q') || '';
    setSearchQuery(query);
  }, [location.search]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const params = new URLSearchParams();
      params.set('q', searchQuery);
      window.history.pushState({}, '', `${location.pathname}?${params}`);
    }
  };

  const { products, pages } = useMemo(() => {
    if (!searchQuery.trim()) return { products: [], pages: [] };
    const q = searchQuery.toLowerCase();
    const matchedProducts = allProducts.filter(p =>
      p.title.toLowerCase().includes(q) ||
      p.desc.toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q) ||
      p.id.toLowerCase().includes(q)
    );
    const matchedPages = allPages.filter(p =>
      p.title.toLowerCase().includes(q) ||
      p.desc.toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q)
    );
    return { products: matchedProducts, pages: matchedPages };
  }, [searchQuery]);

  return (
    <div className="min-h-screen flex flex-col bg-dark-500">
      <Navbar />
      <main className="flex-grow pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h1 className="text-3xl font-bold text-white mb-8">Search</h1>

          <form onSubmit={handleSearchSubmit} className="relative mb-12">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for products, categories, pages..."
              className="pl-12 pr-12 py-6 bg-dark-400/50 border-dark-300 text-white text-lg placeholder:text-gray-500"
              autoFocus
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </form>

          {searchQuery.trim() && products.length === 0 && pages.length === 0 && (
            <div className="text-center py-16">
              <h3 className="text-xl font-medium text-white mb-2">No results found</h3>
              <p className="text-gray-400">Try different keywords or browse the full catalog.</p>
              <Button variant="outline" className="mt-6 border-white/10 text-white hover:bg-dark-300/50" asChild>
                <Link to="/projects">Browse All Products</Link>
              </Button>
            </div>
          )}

          {products.length > 0 && (
            <div className="mb-12">
              <h2 className="text-xl font-semibold text-white mb-6">Products ({products.length})</h2>
              <div className="grid gap-4">
                {products.map(p => (
                  <Link
                    key={p.id}
                    to={`/projects/${p.id}`}
                    className="flex items-center gap-4 p-4 rounded-lg bg-dark-400/50 border border-white/5 hover:border-tasktide-teal/30 transition-colors"
                  >
                    {statusDot(p.status)}
                    <div className="flex-grow">
                      <div className="flex items-center gap-2">
                        <h3 className="text-white font-medium">{p.title}</h3>
                        <span className="text-xs text-gray-500">{p.category}</span>
                      </div>
                      <p className="text-gray-400 text-sm">{p.desc}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-gray-600" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {pages.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-6">Pages ({pages.length})</h2>
              <div className="grid gap-4">
                {pages.map(p => (
                  <Link
                    key={p.url}
                    to={p.url}
                    className="flex items-center gap-4 p-4 rounded-lg bg-dark-400/50 border border-white/5 hover:border-tasktide-teal/30 transition-colors"
                  >
                    <div className="flex-grow">
                      <div className="flex items-center gap-2">
                        <h3 className="text-white font-medium">{p.title}</h3>
                        <span className="text-xs text-gray-500">{p.category}</span>
                      </div>
                      <p className="text-gray-400 text-sm">{p.desc}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-gray-600" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Search;